/**
 * List Navigation Module
 * Alternative navigation displaying dimensions → areas → topics in list format
 * Syncs with TriangleNav StateManager
 */

import TopicScroller from './TopicScroller.js'
import StateRenderer from './StateRenderer.js'

export default class ListNav {
  constructor(app, opts = {}) {
    this.app = app
    this.opts = opts
    this.instances = []

    this.init()
  }

  init() {
    // Find all list nav widgets
    const widgets = document.querySelectorAll('[data-list-nav]')

    widgets.forEach(el => {
      const instance = this.initializeWidget(el)
      if (instance) {
        this.instances.push(instance)
      }
    })

    console.log(`[ListNav] Initialized ${this.instances.length} widget(s)`)
  }

  initializeWidget(el) {
    const instance = {
      el,
      topicScroller: null,
      stateRenderer: null,
      data: null,
      stateManager: null,
      isInitialized: false,
    }

    // Parse state from data attribute
    const state = el.getAttribute('data-list-nav-state')
    console.log('[ListNav] Current state:', state)

    // Get data from triangle nav (shared data attribute)
    const triangleNavWidget = document.querySelector('[data-triangle-nav-widget]')
    if (triangleNavWidget) {
      const dataAttr = triangleNavWidget.getAttribute('data-triangle-nav-data')
      if (dataAttr) {
        try {
          instance.data = JSON.parse(dataAttr)
        } catch (e) {
          console.error('[ListNav] Failed to parse data:', e)
          return null
        }
      }
    }

    if (!instance.data) {
      console.warn('[ListNav] No data available')
      return null
    }

    // Get StateManager from TriangleNav instance
    if (window.triangleNavInstances && window.triangleNavInstances.length > 0) {
      instance.stateManager = window.triangleNavInstances[0].stateManager
      console.log('[ListNav] Using shared StateManager')
    } else {
      console.warn('[ListNav] No StateManager found - ListNav will work but not sync with triangle')
    }

    // Initialize modules
    instance.topicScroller = new TopicScroller(el)
    instance.stateRenderer = new StateRenderer(el, instance.data, instance.topicScroller)

    // Setup click handlers
    this.setupClickHandlers(instance)

    // Listen for state changes from StateManager
    if (instance.stateManager) {
      instance.stateManager.onChange((newState, oldState) => {
        console.log('[ListNav] State changed:', oldState, '->', newState)
        instance.stateRenderer.render(newState)
      })

      // Initial render based on current state
      const currentState = instance.stateManager.getState()
      instance.stateRenderer.render(currentState)
    }

    // Expose instance globally for debugging
    if (!window.listNavInstances) {
      window.listNavInstances = []
    }
    window.listNavInstances.push(instance)

    instance.isInitialized = true
    return instance
  }

  setupClickHandlers(instance) {
    const { el, topicScroller } = instance

    // Dimension link clicks
    el.addEventListener('click', e => {
      // Get stateManager lazily to ensure we have the latest reference
      const triangleNav = window.triangleNavInstances?.[0]
      const stateManager = triangleNav?.stateManager

      const dimensionLink = e.target.closest('[data-dimension-link]')
      if (dimensionLink) {
        this.handleDimensionClick(e, dimensionLink, stateManager)
        return
      }

      // Area link clicks
      const areaLink = e.target.closest('[data-area-link]')
      if (areaLink) {
        this.handleAreaClick(e, areaLink, stateManager)
        return
      }

      // Topic link clicks (smooth scroll only, no navigation)
      const topicLink = e.target.closest('[data-topic-link]')
      if (topicLink) {
        this.handleTopicClick(e, topicLink, topicScroller)
        return
      }
    })
  }

  handleDimensionClick(e, link, stateManager) {
    const handle = link.getAttribute('data-dimension-link')
    const url = link.getAttribute('href')

    console.log('[ListNav] Dimension clicked:', handle, url)

    // Check if View Transitions API is available
    const hasViewTransitions = typeof document.startViewTransition === 'function'

    if (!hasViewTransitions) {
      // Let the link work normally
      return
    }

    // Prevent default navigation and stop propagation to avoid ViewTransitions
    // interceptClicks handler from also catching this click (which would cause
    // double navigation - one from interceptClicks and one from our custom event)
    e.preventDefault()
    e.stopPropagation()

    // Store current state and trigger animation (only for forward transitions)
    if (stateManager) {
      const currentState = stateManager.getState()
      console.log('[ListNav] Current state before navigation:', currentState)
      sessionStorage.setItem('triangleNav:previousState', JSON.stringify(currentState))
      console.log('[ListNav] Stored previousState in sessionStorage')

      // Only animate on current page for START → DIMENSION (forward transition)
      // Other transitions are handled by the new page via stored previousState
      if (currentState.type === 'start') {
        stateManager.goToDimension(handle)
        console.log('[ListNav] Triggered forward animation: start ->', handle)
      }
    } else {
      console.warn('[ListNav] No stateManager available - animation will not work')
    }

    // Dispatch navigation event
    window.dispatchEvent(
      new CustomEvent('triangle-nav-navigate', {
        detail: { url, handle, type: 'dimension', isBackNavigation: false },
      })
    )

    // Fallback to regular navigation if ViewTransitions doesn't handle it
    setTimeout(() => {
      if (!document.body.classList.contains('view-transitions-active')) {
        console.log('[ListNav] FALLBACK NAVIGATION TRIGGERED (dimension click)')
        window.location.href = url
      }
    }, 100)
  }

  handleAreaClick(e, link, stateManager) {
    const handle = link.getAttribute('data-area-link')
    const url = link.getAttribute('href')

    // Get dimension from parent section or current state
    const parentDimension = link.closest('[data-dimension]')?.getAttribute('data-dimension')

    console.log('[ListNav] Area clicked:', handle, url, 'dimension:', parentDimension)

    // Check if View Transitions API is available
    const hasViewTransitions = typeof document.startViewTransition === 'function'

    if (!hasViewTransitions) {
      // Let the link work normally
      return
    }

    // Prevent default navigation and stop propagation to avoid ViewTransitions
    // interceptClicks handler from also catching this click (which would cause
    // double navigation - one from interceptClicks and one from our custom event)
    e.preventDefault()
    e.stopPropagation()

    // Store current state and trigger animation (only for forward transitions)
    if (stateManager) {
      const currentState = stateManager.getState()
      sessionStorage.setItem('triangleNav:previousState', JSON.stringify(currentState))

      // Only animate on current page for DIMENSION → AREA (forward transition)
      // Other transitions are handled by the new page via stored previousState
      if (currentState.type === 'dimension') {
        stateManager.goToArea(handle)
        console.log('[ListNav] Triggered forward animation: dimension ->', handle)
      }
    }

    // Dispatch navigation event
    window.dispatchEvent(
      new CustomEvent('triangle-nav-navigate', {
        detail: { url, handle, type: 'area', isBackNavigation: false },
      })
    )

    // Fallback to regular navigation if ViewTransitions doesn't handle it
    setTimeout(() => {
      if (!document.body.classList.contains('view-transitions-active')) {
        console.log('[ListNav] FALLBACK NAVIGATION TRIGGERED (area click)')
        window.location.href = url
      }
    }, 100)
  }

  handleTopicClick(e, link, topicScroller) {
    e.preventDefault()

    const anchor = link.getAttribute('data-topic-anchor')
    const handle = link.getAttribute('data-topic-link')

    console.log('[ListNav] Topic clicked:', handle, anchor)

    if (anchor) {
      // Find and open the topic toggle
      const topicSlug = anchor.replace('#', '')
      const topicToggle = document.querySelector(`[data-topic="${topicSlug}"]`)

      if (topicToggle) {
        // Find the trigger button and click it to open
        const trigger = topicToggle.querySelector('[data-toggle-trigger]')
        if (trigger && !trigger.hasAttribute('data-toggle-trigger-active')) {
          trigger.click()
          console.log('[ListNav] Opened topic:', topicSlug)
        }
      }

      // Scroll to the topic
      topicScroller.scrollToAnchor(anchor)

      // Update active state on topic buttons
      const allTopics = link.closest('.topics-list').querySelectorAll('[data-topic-link]')
      allTopics.forEach(t => t.classList.remove('is-active'))
      link.classList.add('is-active')

      // Update track circle position
      topicScroller.updateTrackPosition(link)
    }
  }

  destroy() {
    // Clean up instances
    this.instances = []

    // Clean up global reference
    delete window.listNavInstances
  }
}
